import { useState } from 'react';
import { Card } from '../../components/Card';
import { Checkbox, IconButton } from '../../components/ui';
import { clsx } from '../../components/clsx';
import { DENSE_AFTER, type HealthField } from '../../lib/dailies';
import { HealthSettings } from './HealthSettings';
import { useHealth } from './useHealth';

/**
 * 💧 Daily Health Checklist.
 *
 * User-defined fields (persistent) + today's ticks (reset at local midnight
 * by `useDailyState`'s shared clock — no timer is hand-rolled here, and the
 * reset lands live in a pinned tab without a refresh).
 *
 * No API, no keys, no async: there is no loading or error state to design,
 * only 'empty' (no fields configured yet).
 */
export function HealthPanel() {
  const health = useHealth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { done, total, percent } = health.completion;
  const dense = total > DENSE_AFTER;

  return (
    <Card
      title="Daily Health"
      icon="💧"
      accent="health"
      testId="health-panel"
      status={!settingsOpen && total === 0 ? 'empty' : 'ready'}
      emptyMessage="No checklist fields yet."
      hint="Add water intake, a protein goal, movement — whatever you're tracking."
      stateAction={
        <button
          type="button"
          onClick={() => setSettingsOpen(true)}
          className="rounded-lg border border-line px-2.5 py-1 text-xs font-medium text-ink-muted transition-colors hover:bg-panel-raised focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
        >
          Set up fields
        </button>
      }
      action={
        <IconButton
          label={settingsOpen ? 'Close checklist settings' : 'Edit checklist fields'}
          aria-expanded={settingsOpen}
          onClick={() => setSettingsOpen((open) => !open)}
          className={settingsOpen ? 'bg-panel-raised text-ink' : undefined}
        >
          <span aria-hidden className="text-xs">
            {settingsOpen ? '✕' : '⚙'}
          </span>
        </IconButton>
      }
    >
      {settingsOpen ? (
        <HealthSettings health={health} dense={dense} onClose={() => setSettingsOpen(false)} />
      ) : (
        <div className="flex h-full min-h-0 flex-col gap-2">
          <div className="flex shrink-0 items-center gap-2">
            <div
              className="h-1.5 flex-1 overflow-hidden rounded-full bg-panel-raised"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuenow={done}
              aria-label="Checklist completion"
            >
              <div
                className="h-full rounded-full bg-accent-health transition-[width] duration-300"
                style={{ width: `${percent}%` }}
              />
            </div>
            <span className="shrink-0 text-[11px] font-semibold tabular-nums text-ink-muted">
              {done}/{total}
            </span>
          </div>

          <ul className={clsx('flex min-h-0 flex-1 flex-col overflow-hidden', dense ? 'gap-0' : 'gap-0.5')}>
            {health.fields.map((field) => (
              <HealthRow
                key={field.id}
                field={field}
                checked={health.isChecked(field.id)}
                dense={dense}
                onToggle={health.toggle}
              />
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function HealthRow({
  field,
  checked,
  dense,
  onToggle,
}: {
  field: HealthField;
  checked: boolean;
  dense: boolean;
  onToggle: (id: string) => void;
}) {
  return (
    <li
      data-testid="health-row"
      data-checked={checked ? 'true' : 'false'}
      className={clsx('flex items-center gap-2', dense ? 'py-0.5' : 'py-1')}
    >
      <Checkbox checked={checked} onChange={() => onToggle(field.id)} label={field.label} />
      {/* Bigger hit target for the same action; hidden from AT so the row is
          announced once, by the checkbox. */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        onClick={() => onToggle(field.id)}
        className={clsx(
          'min-w-0 flex-1 truncate text-left transition-colors',
          dense ? 'text-[12px]' : 'text-[13px]',
          checked ? 'text-ink-subtle line-through' : 'text-ink',
        )}
      >
        {field.label}
      </button>
    </li>
  );
}
